import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

function genSession() {
  let s = localStorage.getItem("dsvm_chat_session");
  if (!s) {
    s = `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("dsvm_chat_session", s);
  }
  return s;
}

export default function Chatbot() {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const msgIdRef = useRef(0);
  const newMsgId = () => `m-${++msgIdRef.current}`;
  const [messages, setMessages] = useState([
    { id: newMsgId(), role: "assistant", content: t("chat.greeting") },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(genSession());
  const scrollRef = useRef(null);

  // Update greeting when language changes (only if still on initial greeting)
  useEffect(() => {
    setMessages((m) => {
      if (m.length === 1 && m[0].role === "assistant") {
        return [{ id: newMsgId(), role: "assistant", content: t("chat.greeting") }];
      }
      return m;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { id: newMsgId(), role: "user", content: text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { session_id: sessionId.current, message: text, lang });
      setMessages((m) => [...m, { id: newMsgId(), role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [...m, { id: newMsgId(), role: "assistant", content: t("chat.error") }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 flex items-center justify-center bg-[#C5A059] hover:bg-[#D4AF37] text-black shadow-[0_0_24px_rgba(197,160,89,0.4)] transition-all"
        data-testid="chatbot-toggle"
        aria-label={t("chat.title")}
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {open && (
        <div
          className="fixed bottom-24 right-6 z-50 w-[92vw] max-w-md h-[65vh] max-h-[560px] flex flex-col bg-[#0A0A0A] border border-[#C5A059]/40 shadow-2xl"
          data-testid="chatbot-panel"
        >
          <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between bg-[#121212]">
            <div>
              <div className="font-heading font-semibold text-white">{t("chat.title")}</div>
              <div className="text-[10px] uppercase tracking-widest text-[#C5A059]">{t("chat.live")}</div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/60 hover:text-white" data-testid="chatbot-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {messages.map((m) => (
              <div
                key={m.id}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                data-testid={`chat-message-${m.role}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#C5A059] text-black"
                      : "bg-[#1A1A1A] text-white border border-white/10"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-[#1A1A1A] border border-white/10 px-4 py-3 text-white/60">
                  <Loader2 className="w-4 h-4 animate-spin" />
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-white/10 p-3 flex gap-2 bg-[#0A0A0A]">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={t("chat.placeholder")}
              className="flex-1 bg-transparent border border-white/10 focus:border-[#C5A059] outline-none px-3 py-2 text-sm text-white placeholder:text-white/40"
              disabled={loading}
              data-testid="chatbot-input"
            />
            <button
              onClick={send}
              disabled={loading || !input.trim()}
              className="px-3 py-2 bg-[#C5A059] hover:bg-[#D4AF37] text-black disabled:opacity-50"
              data-testid="chatbot-send"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
